export type AwardType = 'NEED' | 'AGI' | 'SAI';
export type CalculatedBy = 'PERCENTAGE' | 'FLAT_AMOUNT';

export const AWARD_TYPE_OPTIONS: Array<{ value: AwardType; label: string }> = [
  { value: 'NEED', label: 'Need' },
  { value: 'AGI', label: 'AGI' },
  { value: 'SAI', label: 'SAI' },
];

export const CALCULATED_BY_OPTIONS: Array<{ value: CalculatedBy; label: string }> = [
  { value: 'PERCENTAGE', label: '%' },
  { value: 'FLAT_AMOUNT', label: 'Flat amount' },
];

export const RANGES_LABEL_BY_AWARD_TYPE: Record<AwardType, string> = {
  NEED: 'Need ranges',
  AGI: 'AGI ranges',
  SAI: 'SAI ranges',
};

export const DEFAULT_ACADEMIC_RATES = [1, 2, 3, 4, 5];

export const SECONDARY_LABEL_BY_CALCULATED_BY: Record<CalculatedBy, string> = {
  PERCENTAGE: '%',
  FLAT_AMOUNT: '$',
};

export const getRangesLabel = (awardType: string): string =>
  RANGES_LABEL_BY_AWARD_TYPE[awardType as AwardType] ?? '';

export const getSecondaryLabel = (calculatedBy: string): string =>
  SECONDARY_LABEL_BY_CALCULATED_BY[calculatedBy as CalculatedBy] ?? '%';

export interface Range {
  from: number | null;
  to: number | null;
}

/**
 * Loose form accepted by helpers below; values may arrive as `undefined` from
 * partial calculation rows or unsanitized API payloads. Helpers normalize
 * undefined → null at runtime via `??`/`==`.
 */
export type RangeLike = {
  from?: number | null;
  to?: number | null;
};

export interface CalculationRow {
  range?: RangeLike | null;
  academicRankId?: number;
  amount?: number;
  discount?: number;
}

export const buildRangeKey = (range: RangeLike): string =>
  `${range.from ?? 'null'}_${range.to ?? 'null'}`;

export const isNonAidRange = (range: RangeLike): boolean =>
  range.from == null && range.to == null;

export const sortRangesForDisplay = (ranges: Range[]): Range[] => {
  const nonAid = ranges.filter(isNonAidRange);
  const numeric = ranges
    .filter((r) => !isNonAidRange(r))
    .sort((a, b) => (a.from ?? 0) - (b.from ?? 0));

  return [...nonAid, ...numeric];
};

export const formatRangeLabel = (range: Range): string => {
  if (isNonAidRange(range)) return 'Non-Aid';

  const from = range.from != null ? `$ ${range.from.toLocaleString()}` : '';
  const to = range.to != null ? range.to.toLocaleString() : '';

  if (from && to) return `${from} - ${to}`;
  if (from) return `${from}+`;

  return `$ ${to}`;
};

export const getUniqueRangesFromCalculation = (
  calculation: CalculationRow[] = []
): Range[] => {
  const seen = new Set<string>();

  return calculation.reduce<Range[]>((acc, row) => {
    const key = `${row.range?.from ?? 'null'}_${row.range?.to ?? 'null'}`;

    if (!seen.has(key) && row.range) {
      seen.add(key);
      acc.push({ from: row.range.from ?? null, to: row.range.to ?? null });
    }

    return acc;
  }, []);
};
